/**
 * 売上見込み(P-68)。ダッシュボードに出す当月の着地見込み。
 * 計算は `salesForecast.ts`(画面は数字を出すだけ)。自分でその月のデータを読む(ダッシュボードの期間に左右されない)。
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import HelpTip from '@core/components/HelpTip';
import Panel from '@core/components/ui/Panel';
import ToneValue from '@core/components/ui/ToneValue';
import type { Company, DailySummary, DateStr, Delivery, MonthTargets } from '@core/lib/types';
import { formatCount, formatCurrency, formatPercent } from '@core/utils/formatters';
import { daysInMonth, staffMetrics } from './calc';
import { costRateTone } from './colors';
import { fetchSummariesRange } from './dashboardRepo';
import { fetchDeliveries } from './deliveryRepo';
import { salesForecast } from './salesForecast';

export default function SalesForecastPanel({ today, companies, targets }: { today: DateStr; companies: Company[]; targets: MonthTargets | null }) {
  const month = today.slice(0, 7);
  const [summaries, setSummaries] = useState<DailySummary[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [loading, setLoading] = useState(true);

  const dates = useMemo(() => {
    const n = daysInMonth(month);
    return Array.from({ length: n }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}` as DateStr);
  }, [month]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const from = dates[0]!;
    const to = dates[dates.length - 1]!;
    void Promise.all([fetchSummariesRange(from, to), fetchDeliveries(from, to)])
      .then(([s, d]) => {
        if (!alive) return;
        setSummaries(s);
        setDeliveries(d);
      })
      .catch(() => undefined)
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [dates]);

  const companyMap = useMemo(() => new Map(companies.map((c) => [c.id, c])), [companies]);
  const rows = useMemo(() => {
    if (!targets) return [];
    return summaries.flatMap((s) => s.staff.map((st) => staffMetrics(st, s, targets, companyMap)));
  }, [summaries, targets, companyMap]);

  const f = useMemo(() => salesForecast({ month, dates, companies, deliveries, rows }), [month, dates, companies, deliveries, rows]);
  const target = targets?.cost_rate_total ?? null;

  return (
    <Panel
      title="今月の売上見込み"
      meta={`${month.slice(0, 4)}年${Number(month.slice(5))}月 ／ ${f.asOf ? `${Number(f.asOf.slice(8))}日まで確定` : '確定した日なし'}`}
      note="**確定した日**(納品件数を入れた日)の 売上 と 人件費 から原価率を出し、**残りの日は会社マスタの納品予定(曜日別)× 単価**で足した着地の見込みです。人件費の見込みは「着地の売上 × 確定分の原価率」です"
    >
      {loading ? (
        <div className="ui-empty">読み込み中…</div>
      ) : f.asOf === null ? (
        <div className="ui-empty">
          今月はまだ納品件数が入っていません。<Link to="/close">締め作業</Link>で入れると見込みが出ます
        </div>
      ) : (
        <>
          <div className="forecast-grid">
            <div className="forecast-box">
              <div className="forecast-label">ここまでの実績({formatCount(f.days_done, { unit: '日' })})</div>
              <div className="forecast-value">{formatCurrency(f.actual.revenue)}</div>
              <div className="forecast-sub">
                納品 {formatCount(f.actual.count, { unit: '件' })} ／ 人件費 {formatCurrency(f.actual.pay)} ／ 原価率{' '}
                <ToneValue tone={costRateTone(f.actual.rate, target)}>{formatPercent(f.actual.rate)}</ToneValue>
              </div>
            </div>
            <div className="forecast-box">
              <div className="forecast-label">残り{formatCount(f.days_left, { unit: '日' })}の予定</div>
              <div className="forecast-value">{f.no_plan ? '—' : formatCurrency(f.plan.revenue)}</div>
              <div className="forecast-sub">
                {f.no_plan ? (
                  <>
                    会社マスタに納品予定がありません(<Link to="/settings/delivery-plan">設定 → 納品件数の予定</Link>)
                  </>
                ) : (
                  <>予定の納品 {formatCount(f.plan.count, { unit: '件' })}</>
                )}
              </div>
            </div>
            <div className="forecast-box is-landing">
              <div className="forecast-label">
                <HelpTip
                  help={{
                    title: '着地の見込み',
                    what: '確定した売上 ＋ 残りの日の予定(会社マスタの納品予定 × その日の単価)。人件費は「着地の売上 × 確定分の原価率」',
                    better: '売上・粗利は高いほど良い(原価率は低いほど良い)',
                  }}
                >
                  着地の見込み
                </HelpTip>
              </div>
              <div className="forecast-value">{formatCurrency(f.landing.revenue)}</div>
              <div className="forecast-sub">
                納品 {formatCount(f.landing.count, { unit: '件' })} ／ 人件費(見込) {formatCurrency(f.landing.pay)} ／ 粗利(見込) {formatCurrency(f.landing.gross)}
              </div>
            </div>
          </div>
        </>
      )}
    </Panel>
  );
}
